/*!
  Pioneer WFRIA BadgeButton v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["BadgeButton"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("BadgeButton") < 0) {
    window.__PIONEER_GLOBAL__.components.push("BadgeButton");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["aria-label", "badge", "badgeAbsolutePosition", "children", "className", "counter", "icon", "kind"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import clsx from "clsx";
import PropTypes from "prop-types";
import React, { forwardRef, useImperativeHandle, useRef } from "react";
import { BUTTON_STYLES, BUTTON_TYPES } from "../common/constants";
import { validIcon } from "../common/proptypes";
import Badge from "../private/Badge/Badge";
import BaseButton from "../private/BaseButton/BaseButton";
import extractTheme from "./extractTheme";
/**
 *
 * BadgeButton is a React component that provides a Button or Icon Button with a count.
 *
 */

import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const BadgeButton = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    "aria-label": triggerAriaLabel,
    badge,
    badgeAbsolutePosition,
    children,
    className,
    counter,
    icon,
    kind
  } = _ref,
      otherProps = _objectWithoutProperties(_ref, _excluded);

  const theme = useTheme();
  const buttonRef = useRef();

  if (badge && counter) {
    throw new Error("BadgeButton cannot use both badge and counter");
  }

  const buttonClassNames = clsx("pnr-badge-button", className);
  const dataCount = badge || counter;
  let type;

  if (icon && !dataCount) {
    type = "iconEmpty";
  } else if (icon) {
    type = "icon";
  } else if (!dataCount) {
    type = "buttonEmpty";
  } else {
    type = "button";
  }

  const {
    cssRoot,
    cssBody,
    cssPosition
  } = extractTheme({
    theme,
    type
  });
  const position = badgeAbsolutePosition || cssPosition;
  useImperativeHandle(ref, () => ({
    focus: () => buttonRef.current.focus()
  }));

  const buttonElement = _jsxs(BaseButton, _objectSpread(_objectSpread({
    noAriaDescribedby: true,
    className: buttonClassNames,
    css: cssBody,
    ref: buttonRef,
    "aria-label": triggerAriaLabel,
    kind: icon ? "standard" : kind,
    centerIcon: icon
  }, otherProps), {}, {
    children: [children, !icon && _jsx(Badge, {
      type: counter ? "counter" : "badge",
      count: dataCount,
      position: position
    })]
  }));

  return icon ? _jsxs("div", {
    className: "pnr-badge-button-container",
    style: cssRoot,
    children: [buttonElement, _jsx(Badge, {
      type: "badge",
      count: dataCount,
      position: position,
      "aria-hidden": "true"
    })]
  }) : buttonElement;
});
BadgeButton.propTypes = process.env.NODE_ENV !== "production" ? {
  /** aria-label for the BadgeButton. */
  "aria-label": PropTypes.string.isRequired,

  /** Provides a badge with a count of items. */
  badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Optional positioning for absolute positioned Badge. */
  badgeAbsolutePosition: PropTypes.shape({
    /** Top position for Badge. */
    top: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    /** Right position for Badge. */
    right: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    /** Bottom position for Badge. */
    bottom: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),

    /** Left position for Badge. */
    left: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }),

  /** This is what will be displayed inside the button. */
  children: PropTypes.node,

  /** The CSS class name of the root element. */
  className: PropTypes.string,

  /** Provides a label with a count of items. */
  counter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /** Disable or enable the look of a button. */
  disabled: PropTypes.bool,

  /** SvgIcon to be used for Icon BadgeButton. */
  icon: validIcon,

  /** Button has a a different set of `kinds`. */
  kind: PropTypes.oneOf(BUTTON_STYLES),

  /** HTML button type. */
  type: PropTypes.oneOf(BUTTON_TYPES)
} : {};
BadgeButton.defaultProps = {
  badge: null,
  badgeAbsolutePosition: null,
  children: null,
  className: "",
  counter: null,
  disabled: false,
  icon: null,
  kind: null,
  type: "button"
};
BadgeButton.displayName = "BadgeButton";
export default BadgeButton;